import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as loginService, logout as logoutService, getCurrentUser, isAuthenticated } from '../services/auth';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getCurrentUser());
  const [loading, setLoading] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState(localStorage.getItem('branch_id') || 'all');
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    // Check if user is logged in on mount
    if (isAuthenticated()) {
      const savedUser = getCurrentUser();
      if (savedUser) {
        setUser(savedUser);
        // If not an owner and has a branch, force that branch
        if (savedUser.role !== 'owner' && savedUser.role !== 'admin' && savedUser.branch_id) {
          setSelectedBranchId(savedUser.branch_id.toString());
          localStorage.setItem('branch_id', savedUser.branch_id.toString());
        }
      }

      // Fetch fresh profile from server in background
      api.get('/auth.php/me')
        .then(response => {
          if (response && (response.user || response.id)) {
            const freshUser = response.user || response;
            setUser(freshUser);
            localStorage.setItem('user', JSON.stringify(freshUser));
          }
        })
        .catch(err => {
          console.error('Failed to fetch fresh user profile:', err);
        });
    }
    setLoading(false);
  }, []);

  const switchBranch = (branchId) => {
    setSelectedBranchId(branchId);
    if (branchId === 'all') {
      localStorage.removeItem('branch_id');
    } else {
      localStorage.setItem('branch_id', branchId);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await loginService(username, password);
      setUser(response.user || getCurrentUser());
      return response;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await logoutService();
      setUser(null);
    } catch (error) {
      // Even if logout fails, clear local state
      setUser(null);
      throw error;
    }
  };

  const updateCurrentUser = (updatedData) => {
    setUser(prev => {
      const newUser = { ...prev, ...updatedData };
      localStorage.setItem('user', JSON.stringify(newUser));
      return newUser;
    });
  };

  const refreshUser = async () => {
    try {
      const savedUser = getCurrentUser();
      if (savedUser) {
        setUser(savedUser);
      }
    } catch (e) {
      console.error('Failed to refresh user:', e);
    }
  };

  const value = {
    user,
    setUser,
    updateCurrentUser,
    refreshUser,
    login,
    logout,
    loading,
    selectedBranchId,
    setSelectedBranchId: switchBranch,
    isOwner: user?.role === 'owner' ||
      user?.role === 'admin' ||
      user?.role_name?.toLowerCase() === 'owner' ||
      user?.role_name?.toLowerCase() === 'admin' ||
      user?.role_name === 'ئەدمین' ||
      user?.role_name === 'خاوەن' ||
      user?.role_name === 'خاوەن کۆمپانیا',
    isAuthenticated: isAuthenticated()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

